import { AxiosResponse } from 'axios'
import { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh'
import * as z from 'zod'
import { UserOptionsMe } from '~/shared/api/core/store'
import { Region } from '~/shared/api/region'
import { Role } from '~/shared/api/role'
import { LocaleEnum, TimezoneCode } from '~/shared/config/enums'
import {
  dateSchema,
  enumOptionSchema,
  phoneScheme,
  uuidOptionsSchema,
} from '~/shared/lib/schemas'
import { buildI18nMessage } from '../lib/i18nMessageHelper'
import { ApiModel, ToManyRelation } from './core'

const attributeSchema = z.object({
  email: z.string().email(buildI18nMessage('validate.invalid email address')),
  password: z.string().min(5, {
    message: buildI18nMessage(
      'validate.password must be at least 5 characters long',
    ),
  }),
  name: z.string(),
  phone: phoneScheme,
  telegramUserId: z.number().nullable(),
  timezone: enumOptionSchema(TimezoneCode),
  locale: enumOptionSchema(LocaleEnum),
})

const relationsSchema = z.object({
  regionsIds: uuidOptionsSchema,
  rolesIds: uuidOptionsSchema,
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributeSchema)
  .merge(relationsSchema)

export type UserAttributes = z.infer<typeof schema>

export class User extends ApiModel<typeof schema> {
  static jsonApiType = 'users'

  static schema = schema

  getEmail(): string {
    return this.getAttribute('email')
  }
  getName(): string | undefined {
    return this.getAttribute('name')
  }
  getPhone(): string | undefined {
    return this.getAttribute('phone')
  }
  getTimezone(): TimezoneCode {
    return this.getAttribute('timezone')
  }
  getLocale(): LocaleEnum {
    return this.getAttribute('locale')
  }

  regions(): ToManyRelation<Region, this> {
    return this.hasMany(Region)
  }
  getRegions(): Region[] {
    return this.getRelation('regions') ?? []
  }

  roles(): ToManyRelation<Role, this> {
    return this.hasMany(Role)
  }
  getRoles(): Role[] {
    return this.getRelation('roles') ?? []
  }

  static async login(credentials: { email: Email; password: string }) {
    const url = `${this.getJsonApiUrl()}/login`
    const client = this.httpClient.getImplementingClient()
    return client
      .post<typeof credentials, AxiosResponse<{ token: string }>>(
        url,
        credentials,
        {
          skipAuthRefresh: true,
          headers: {
            Accept: 'application/json',
            'Content-type': 'application/json',
          },
        } as AxiosAuthRefreshRequestConfig,
      )
      .then(({ data }) => data)
  }

  static async me() {
    const url = `${this.getJsonApiUrl()}/me`
    const client = User.httpClient.getImplementingClient()
    return client.get<UserOptionsMe>(url).then(({ data }) => data)
  }
}
